<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useNotificationsStore } from '@/stores/notifications'
import { useUiStore } from '@/stores/ui'
import NotificationItem from './NotificationItem.vue'

const { t } = useI18n()
const router = useRouter()
const notificationsStore = useNotificationsStore()
const uiStore = useUiStore()

const dropdownRef = ref(null)
const bellRef = ref(null)
const isOpen = ref(false)

const displayCount = computed(() => {
  const count = notificationsStore.unreadCount
  if (count > 99) return '99+'
  if (count > 9) return '9+'
  return count
})

const recentNotifications = computed(() => {
  return notificationsStore.notifications.slice(0, 5)
})

const hasNotifications = computed(() => {
  return notificationsStore.notifications.length > 0
})

function toggleDropdown() {
  isOpen.value = !isOpen.value
  if (isOpen.value && notificationsStore.notifications.length === 0) {
    notificationsStore.fetchNotifications()
  }
}

function closeDropdown() {
  isOpen.value = false
}

async function handleMarkAsRead(id) {
  const result = await notificationsStore.markAsRead(id)
  if (result.success) {
    uiStore.showToast(t('notifications.markedAsRead'), 'success')
  }
}

async function handleDelete(id) {
  const result = await notificationsStore.deleteNotification(id)
  if (result.success) {
    uiStore.showToast(t('notifications.deleted'), 'success')
  }
}

async function handleMarkAllAsRead() {
  const result = await notificationsStore.markAllAsRead()
  if (result.success) {
    uiStore.showToast(t('notifications.allMarkedAsRead'), 'success')
  }
}

function goToNotifications() {
  closeDropdown()
  router.push('/notifications')
}

function handleClickOutside(event) {
  if (
    isOpen.value &&
    dropdownRef.value &&
    bellRef.value &&
    !dropdownRef.value.contains(event.target) &&
    !bellRef.value.contains(event.target)
  ) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  // Fetch unread count on mount
  notificationsStore.fetchUnreadCount()
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div class="notification-bell">
    <button
      ref="bellRef"
      class="bell-button"
      :class="{ active: isOpen }"
      :aria-label="t('notifications.title')"
      @click="toggleDropdown"
    >
      🔔
      <span 
        v-if="notificationsStore.unreadCount > 0" 
        class="badge"
      >
        {{ displayCount }}
      </span>
    </button>
    
    <Transition name="dropdown">
      <div 
        v-if="isOpen" 
        ref="dropdownRef"
        class="dropdown"
      >
        <div class="dropdown-header">
          <h3 class="dropdown-title">{{ t('notifications.title') }}</h3>
          <button
            v-if="notificationsStore.hasUnread"
            class="mark-all-btn"
            @click="handleMarkAllAsRead"
          >
            {{ t('notifications.markAllRead') }}
          </button>
        </div>
        
        <div class="dropdown-content">
          <div v-if="notificationsStore.loading" class="loading-state">
            {{ t('app.loading') }}
          </div>
          
          <div v-else-if="!hasNotifications" class="empty-state">
            <span class="empty-icon">🔔</span>
            <p>{{ t('notifications.noNotifications') }}</p>
          </div>
          
          <template v-else>
            <NotificationItem
              v-for="notification in recentNotifications"
              :key="notification.id"
              :notification="notification"
              compact
              @read="handleMarkAsRead"
              @delete="handleDelete"
            />
          </template>
        </div>
        
        <div class="dropdown-footer">
          <button class="view-all-btn" @click="goToNotifications">
            {{ t('notifications.viewAll') }}
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.notification-bell {
  position: relative;
}

.bell-button {
  position: relative;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 10px;
  font-size: 1.25rem;
  cursor: pointer;
  transition: background 0.2s;
}

.bell-button:hover,
.bell-button.active {
  background: var(--color-background-secondary);
}

.badge {
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: var(--color-danger);
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 380px;
  max-height: 480px;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  box-shadow: 0 8px 32px var(--color-shadow);
  z-index: 1000;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--color-border);
}

.dropdown-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text);
}

.mark-all-btn {
  padding: 6px 12px;
  background: transparent;
  border: none;
  color: var(--color-primary);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.2s;
}

.mark-all-btn:hover {
  background: var(--color-primary-light);
}

.dropdown-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.loading-state,
.empty-state {
  padding: 32px 16px;
  text-align: center;
  color: var(--color-text-secondary);
}

.empty-icon {
  display: block;
  font-size: 2.5rem;
  margin-bottom: 12px;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 0.9rem;
}

.dropdown-footer {
  padding: 12px 16px;
  border-top: 1px solid var(--color-border);
}

.view-all-btn {
  width: 100%;
  padding: 10px;
  background: var(--color-background-secondary);
  border: none;
  border-radius: 8px;
  color: var(--color-text);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
}

.view-all-btn:hover {
  background: var(--color-border);
}

/* Dropdown animation */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 480px) {
  .dropdown {
    position: fixed;
    top: 64px;
    left: 10px;
    right: 10px;
    width: auto;
    max-height: calc(100vh - 80px);
  }
}
</style>
